import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';

interface Props {
    children: ReactNode;
}

interface State {
    hasError: boolean;
    error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
    public state: State = {
        hasError: false,
        error: null
    };

    public static getDerivedStateFromError(error: Error): State {
        return { hasError: true, error };
    }

    public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        console.error('Uncaught error:', error, errorInfo);
    }

    public render() {
        if (this.state.hasError) {
            const err = this.state.error;
            const isInterfaceError = err && (
                err.name === 'ReferenceError' || err.name === 'TypeError'
                || err.message?.includes('is not defined')
                || err.message?.includes('is not a function')
            );
            const subtitle = isInterfaceError
                ? 'An unexpected interface error occurred. Reload the dashboard.'
                : 'The dashboard encountered an unexpected data issue. Reload the dashboard.';

            return (
                <div className="flex flex-col items-center justify-center h-full min-h-[400px] bg-slate-50 dark:bg-slate-900 p-8 rounded-xl border border-red-200 dark:border-red-900/30 text-center">
                    <div className="p-4 bg-red-100 dark:bg-red-900/20 rounded-full mb-4 animate-pulse">
                        <AlertTriangle className="w-12 h-12 text-red-500 dark:text-red-400" />
                    </div>
                    <h2 className="text-xl font-bold text-slate-800 dark:text-slate-100 mb-2">
                        Dashboard unavailable
                    </h2>
                    <p className="text-slate-500 dark:text-slate-400 max-w-md mb-6">
                        {subtitle}
                    </p>
                    <button
                        onClick={() => window.location.reload()}
                        className="flex items-center gap-2 px-6 py-2.5 bg-slate-900 dark:bg-white text-white dark:text-slate-900 rounded-lg hover:opacity-90 transition-opacity font-medium"
                    >
                        <RefreshCw className="w-4 h-4" />
                        Reload Dashboard
                    </button>
                    {import.meta.env.DEV && this.state.error && (
                        <div className="mt-8 p-4 bg-slate-950 rounded-lg text-left w-full max-w-2xl overflow-auto border border-slate-800">
                            <code className="text-xs font-mono text-red-300">
                                {this.state.error.toString()}
                            </code>
                        </div>
                    )}
                </div>
            );
        }

        return this.props.children;
    }
}
